import { sortRatingScales } from '../misc/rating_scales'
import CurrencyUSD from '../misc/currency_formats'
import { getExtraItems, getMissingItems } from './sale_listing_helpers'

class SaleItem {
  constructor(item) {
    const { saleListing } = item
    const {
      id = '',
      saleProduct = {},
      price = '',
      quantityAvailable = '',
      uniqueSaleProduct = {}
    } = saleListing

    this.id = id
    this.quantity = item.quantity
    this.hasWarranty = !!item.hasWarranty // need to coerce null to boolean here
    this.saleListing = { ...saleListing }

    // sale listing product pages don't have data for {saleProduct}
    this.productUrl = saleProduct?.urls?.storefront || ''
    this.imageUrl = saleProduct?.product?.photo?.baseballCardUrl || ''
    this.name = saleProduct?.product?.fullName || ''
    this.warranty = { ...saleProduct?.warranty }

    this.price = price // needed for sale_carts_controller
    this.productPrice = price // needed for jsx

    this.uniqueListing = !!Object.keys(uniqueSaleProduct).length
    this.noLongerAvailable = quantityAvailable === 0
    this.quantityAvailable = (quantityAvailable > 0 && this.uniqueListing) ? 1 : quantityAvailable // don't overwrite 0 available
    this.availabilityLabel = this.getAvailabilityLabel()

    let overallRating = ''
    let simpleRatings = []
    if (saleListing.saleProduct?.ratingScales) {
      const sortedRatingScales = sortRatingScales(saleListing.saleProduct.ratingScales)
      const ratingIds = saleListing.ratings.map(rating => rating.id)
      const ratingScales = sortedRatingScales.map(scale => {
        return {
          name: scale.name,
          rating: scale.ratings.find(rating => ratingIds.includes(rating.id))
        }
      })

      overallRating = ratingScales.find(scale => scale.name === 'Overall').rating
      simpleRatings = ratingScales.map(scale => {
        if (['Cosmetic', 'Performance', 'Glass'].includes(scale.name)) {
          return {
            name: scale.name,
            value: scale.rating.rank,
            varName: scale.rating.name.replace(/\s+/g, '-').toLowerCase()
          }
        } else { return null }
      }).filter(rating => !!rating)
    }
    this.overallRating = overallRating
    this.simpleRatings = simpleRatings

    this.missingItems = getMissingItems(saleListing)
    this.extraItems = getExtraItems(saleListing)

    this.correctionMessage = item.correctionMessage || ''

    // This is a weird hack for when quantity drops to 0 then the methods
    // defined below are undefined because instead of instantiating another SaleItem
    // when replacing this in the cart we are spreading `{...this}` which only transfers
    // the attributes on the constructor of the class.
    // ESLint does NOT like defining this.thing = this.thing
    /* eslint-disable no-self-assign */
    this.getAvailabilityLabel = this.getAvailabilityLabel
    this.withQuantityUpdated = this.withQuantityUpdated
    this.withWarrantyUpdated = this.withWarrantyUpdated
    /* eslint-enable no-self-assign */
  }

  getAvailabilityLabel() {
    if (this.noLongerAvailable) {
      return 'SOLD OUT'
    }
    if (this.saleListing.lowStock || this.uniqueListing) {
      return 'Low Stock'
    }
    return ''
  }

  withQuantityUpdated(newQuantity) {
    const newItem = { ...this }
    newItem.quantity = newQuantity
    return newItem
  }

  withWarrantyUpdated(includeWarranty) {
    const newItem = { ...this }
    newItem.hasWarranty = includeWarranty
    return newItem
  }
}

class SalePlan {
  constructor(data) {
    this.type = 'sale'

    // item related
    this.cartObject = {}
    data.cart.forEach(item => (this.cartObject[item.saleListing.id] = new SaleItem(item)))
    this.cart = Object.values(this.cartObject)

    // shipping related
    this.shipType = data.shipType || 'delivery'
    this.shipTypes = [
      { name: 'Delivery', value: 'delivery' },
      { name: 'FedEx Office', value: 'fedex_office' },
      { name: 'Pickup', value: 'pickup' }
    ]
    this.zipCode = data.zipCode
    this.preferredShippingMethod = data.preferredShippingMethod
    this.shippingMethods = data.shippingMethods?.map(shippingMethod => {
      const { shippingMethod: name, price } = shippingMethod
      return ({
        ...shippingMethod,
        shippingLabel: `${name} - ${CurrencyUSD({ amount: price })}`
      })
    }) || []
    const defaultShippingMethod = this.shippingMethods.find(method => method.shippingMethod === 'FedEx Standard')
    this.shipping = data.shipping?.id ? { ...data.shipping } : { ...defaultShippingMethod } || {}
    this.shippingDiscount = this.shipping.discount || 0

    // prices & checkout related
    this.itemSubtotal = data.itemSubtotal
    this.warrantyPrice = this.cart.reduce((accumulator, currentValue) => {
      if (currentValue.hasWarranty) {
        return (accumulator + (currentValue.warranty.price * currentValue.quantity))
      } else {
        return (accumulator)
      }
    }, 0)

    this.subtotal = this.itemSubtotal // ?? plus HD or something ??
    this.total = this.subtotal + this.warrantyPrice + (this.shipping.price - this.shippingDiscount) || '0'

    this.outOfStockItems = this.getOutOfStockItems(this.cart)
    this.cannotCheckout = !!this.outOfStockItems.length

    // display $0 for all prices if there are no items in the cart
    if (!this.cart.length) {
      this.subtotal = 0
      this.itemSubtotal = 0
      this.warrantyPrice = 0
      this.shipping.price = 0
      this.total = 0
    }

    // Analytics object
    this.pipetteCartObject = {
      subtotal: data.itemSubtotal || 0.00,
      items: data.cart.map( (cartItem) => {
        const productPrice = cartItem.saleListing?.price || 0.00
        const protectionPrice = cartItem.hasWarranty ? cartItem.saleListing?.saleProduct?.warranty?.price : 0.00
        return {
          id: cartItem.saleListing?.saleProduct?.product?.number,
          invoice: {
            product_price: productPrice,
            protection_price: protectionPrice,
            subtotal: productPrice + protectionPrice
          },
          quantity: cartItem.quantity,
          sale_listing_id: cartItem.saleListing.id
        }
      })
    }
  }

  // items related
  withItemsUpdated(newItemsObject) {
    Object.keys(newItemsObject).forEach((key) => {
      const item = newItemsObject[key]
      const itemQuantity = parseInt(item.quantity)
      const numberAvailable = parseInt(item.quantityAvailable)
      let finalQuantity = itemQuantity
      let correctionMessage

      if (itemQuantity < 0) {
        finalQuantity = '1'
        correctionMessage = 'Minimum item quantity is 1.'
      }

      if (itemQuantity > 100) {
        finalQuantity = '100'
        correctionMessage = 'Maximum item quantity is 100.'
      }

      if (numberAvailable && (itemQuantity > numberAvailable)) {
        finalQuantity = item.quantityAvailable
        correctionMessage = `There ${item.quantityAvailable === '1' ? `is only ${item.quantityAvailable} listing` : `are only ${item.quantityAvailable} listings`} available for this item.`
      }

      item.quantity = finalQuantity
      item.correctionMessage = correctionMessage

      // remove items with no quantity
      if (newItemsObject[key].quantity === 0) {
        delete newItemsObject[key]
      }
    })

    const newCart = Object.values(newItemsObject)

    return Object.assign(this, {
      cartObject: newItemsObject,
      cart: newCart,
      outOfStockItems: this.getOutOfStockItems(newCart),
      cannotCheckout: !!this.getOutOfStockItems(newCart).length
    })
  }

  // checkout related
  getOutOfStockItems(cart) {
    const outOfStock = cart.filter(item => !!item.noLongerAvailable)
    return outOfStock
  }
}

export default SalePlan
export { SaleItem }
